import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { productAPI } from '../../api/services';
import useDebounce from '../../hooks/useDebounce';

export default function SearchBar({ onClose }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounced = useDebounce(query, 300);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!debounced || debounced.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    productAPI.getSuggestions(debounced)
      .then(res => setSuggestions(res.data.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [debounced]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/products?search=${encodeURIComponent(query.trim())}`);
    onClose?.();
  };

  const handleSelect = (product) => {
    navigate(`/products/${product.slug || product._id}`);
    onClose?.();
  };

  const showDropdown = focused && (loading || suggestions.length > 0 || debounced.length >= 2);

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      {/* Input Header Row */}
      <div className="flex items-center gap-3">
        <form onSubmit={handleSearch} className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder="Search fragrances, notes, occasions..."
            className="w-full bg-[#04110c]/80 border border-white/10 text-[#f5efe4] placeholder-cream/30 pl-5 pr-14 py-4 text-sm tracking-wide focus:outline-none focus:border-gold-500/50 backdrop-blur-xl transition-all duration-300"
          />
          <button
            type="submit"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gold-500 hover:text-gold-400 transition-colors"
            aria-label="Submit search"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </form>

        {/* Premium Explicit Touch Screen Close Button Target */}
        <button
          type="button"
          onClick={onClose}
          className="bg-white/5 border border-white/10 p-4 hover:border-gold-500/40 text-cream/70 hover:text-gold-400 transition-all duration-300 flex items-center justify-center"
          aria-label="Close search overlay"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Suggestion Dropdown Overlays */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#04110c] border border-white/5 shadow-2xl z-50 max-h-80 overflow-y-auto backdrop-blur-2xl">
          {loading ? (
            <div className="px-5 py-5 flex items-center gap-3">
              <div className="w-4 h-4 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-[#f5efe4]/40 text-xs tracking-widest uppercase font-body">Searching...</span>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map(product => (
              <button
                key={product._id}
                onClick={() => handleSelect(product)}
                className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-white/[0.03] border-b border-white/5 last:border-0 transition-colors text-left"
              >
                {/* Product Frame image structure */}
                <div className="w-10 h-12 bg-white/5 border border-white/10 flex-shrink-0 overflow-hidden relative">
                  {product.images?.[0]?.url ? (
                    <img src={product.images[0].url} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-b from-[#04110c] to-black/40" />
                  )}
                </div>

                {/* Fragrance details block */}
                <div className="flex-1 min-w-0">
                  <p className="text-[#f5efe4] text-sm font-body tracking-wide truncate">{product.name}</p>
                  <p className="text-gold-500/60 text-[10px] tracking-[0.2em] uppercase font-body mt-0.5">{product.category}</p>
                </div>
                <span className="text-gold-400 text-sm font-body tracking-wide flex-shrink-0 ml-2">
                  ₹{product.price?.toLocaleString()}
                </span>
              </button>
            ))
          ) : debounced.length >= 2 ? (
            <div className="px-5 py-5">
              <p className="text-cream/40 text-xs tracking-widest uppercase font-body">No matches found for "{debounced}"</p>
            </div>
          ) : null}

          {/* Quicklink Bottom View-All Footer banner */}
          {suggestions.length > 0 && (
            <button
              onClick={handleSearch}
              className="w-full px-5 py-3.5 text-gold-400 text-xs tracking-[0.15em] uppercase font-body hover:bg-white/[0.03] text-left border-t border-white/5 transition-colors flex justify-between items-center group"
            >
              <span>See all results for "{query}"</span>
              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}